import { useEffect, useState } from 'react';
import { IconButton, Box, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { b2bColors } from '../styles/b2bTheme';

/**
 * Ramène une quantité saisie librement sur la grille du case_pack.
 *
 * On ne vend qu'en caisses complètes : 21 unités d'un pack de 20 n'existe
 * pas. On arrondit au multiple le plus proche (21 → 20, 31 → 40) plutôt
 * qu'au supérieur, pour que corriger une frappe ne double pas la ligne.
 * Le backend arrondit lui aussi au pack supérieur à la soumission
 * (OrderService) — ce garde-fou-ci est de l'UX, pas la source de vérité.
 */
export function snapToPack(raw, { casePack = 1, min = 0, max = 9999 } = {}) {
    const step = casePack > 0 ? Math.floor(casePack) : 1;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return min;
    if (step <= 1) return Math.min(max, Math.max(min, Math.round(n)));

    const maxPacks = Math.floor(max / step);
    const minPacks = Math.max(1, Math.ceil(min / step));
    const packs = Math.min(maxPacks, Math.max(minPacks, Math.round(n / step)));
    return packs * step;
}

/**
 * Qty stepper with optional pack-size semantics. We keep +/- buttons
 * large enough (44x44 target) for touch and surface the case_pack so
 * the user understands why the qty jumps.
 *
 * La saisie libre reste permise pendant la frappe (sinon impossible
 * d'effacer pour retaper) et est ramenée sur la grille du pack au blur
 * ou à Entrée.
 */
export default function QuantityStepper({
    value,
    onChange,
    casePack = 1,
    min = 0,
    max = 9999,
    ariaLabel = 'Quantité',
}) {
    const step = casePack > 0 ? casePack : 1;
    const [draft, setDraft] = useState(String(value));

    // Le parent reste la source de vérité : +/-, snap et reset après
    // « Ajouter » doivent se refléter dans le champ.
    useEffect(() => { setDraft(String(value)); }, [value]);

    // `max` n'est pas forcément un multiple du pack : on plafonne au dernier
    // multiple valide, sinon « + » sortirait de la grille en bout de course
    // (pack de 20, max 9999 → 9999, que le backend arrondirait à 10 000).
    const maxOnGrid = step > 1 ? Math.floor(max / step) * step : max;

    const dec = () => onChange(Math.max(min, value - step));
    const inc = () => onChange(Math.min(maxOnGrid, value + step));

    const commit = () => {
        const snapped = snapToPack(draft, { casePack, min, max });
        setDraft(String(snapped));
        if (snapped !== value) onChange(snapped);
    };

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', border: `1px solid ${b2bColors.borderStrong}`, borderRadius: 0.5 }}>
            <IconButton
                onClick={dec}
                aria-label="Diminuer"
                size="small"
                sx={{ borderRadius: 0, minWidth: 36, minHeight: 36 }}
                disabled={value <= min}
            >
                <RemoveIcon fontSize="small" />
            </IconButton>
            <TextField
                value={draft}
                onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
                inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', fontVariantNumeric: 'tabular-nums', width: 50 }, 'aria-label': ariaLabel }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{ minWidth: 60 }}
            />
            <IconButton
                onClick={inc}
                aria-label="Augmenter"
                size="small"
                sx={{ borderRadius: 0, minWidth: 36, minHeight: 36 }}
                disabled={value >= maxOnGrid}
            >
                <AddIcon fontSize="small" />
            </IconButton>
        </Box>
    );
}
